import { Stack, Typography } from '@mui/joy';
import { At, Boat, OfficeChair, PhoneCall, Storefront, UsersThree } from '@phosphor-icons/react';
import { t } from 'i18next';

import { useAuthUser } from 'auth/hooks';
import { ContactGroupType } from 'contacts/enums';
import { IContact } from 'contacts/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Email, PhoneNumber } from 'core/elements';
import { AlternateVersionHelper } from 'core/helpers';
import { theme } from 'theme';
import { Level, Size } from 'theme/enums';

import { Location } from './Location';

const DEFAULT_START_DECORATOR_STYLE = { fill: theme.palette.neutral[500] };

interface IProps {
  contact: Partial<IContact>;
  contactGroupType?: ContactGroupType;
}

export const Contact = ({ contact, contactGroupType }: IProps) => {
  const { user } = useAuthUser();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  return (
    <Stack gap={1}>
      {getContactInfo(contact, contactGroupType, isAlternateVersionEnabled)
        .filter((info) => !info.hide)
        .map((info, index) => (
          <Stack key={index}>
            <Typography
              level={Level.TITLE_SM}
              startDecorator={info.startDecorator}
              sx={{ marginBottom: info?.extra ? '8px' : 0 }}
            >
              {info.value || DEFAULT_PLACEHOLDER}
            </Typography>

            <Stack sx={{ paddingLeft: '24px' }}>{info?.extra}</Stack>
          </Stack>
        ))}
    </Stack>
  );
};

interface IContactInfo {
  startDecorator: JSX.Element;
  value: string | JSX.Element;
  extra?: JSX.Element;
  hide?: boolean;
}

const getContactInfo = (
  contact: Partial<IContact>,
  contactGroupType: ContactGroupType = ContactGroupType.CUSTOMER,
  isAlternateVersionEnabled = false
): IContactInfo[] => {
  return [
    {
      startDecorator: <At style={DEFAULT_START_DECORATOR_STYLE} />,
      value: <Email email={contact.email} />,
    },
    {
      startDecorator: <PhoneCall style={DEFAULT_START_DECORATOR_STYLE} />,
      value: <PhoneNumber phone={contact.phone} />,
    },
    {
      startDecorator: <UsersThree style={DEFAULT_START_DECORATOR_STYLE} />,
      value: contact.contactGroupingName,
      hide: isAlternateVersionEnabled,
    },
    {
      startDecorator: <OfficeChair style={DEFAULT_START_DECORATOR_STYLE} />,
      value: `${t('labelBillTo')} ${contact.billingAddress.name}`,
      extra: (
        <Location
          contactGroupAddress={contact.billingAddress}
          contactGroupType={contactGroupType}
          size={Size.SM}
        />
      ),
    },
    {
      startDecorator: <Boat style={DEFAULT_START_DECORATOR_STYLE} />,
      value: `${t('labelShipTo')} ${contact.shippingAddress.name}`,
      extra: (
        <Location
          contactGroupAddress={contact.shippingAddress}
          contactGroupType={contactGroupType}
          size={Size.SM}
        />
      ),
    },
    {
      startDecorator: <Storefront style={DEFAULT_START_DECORATOR_STYLE} />,
      value: `${t('labelSoldTo')} ${contact.sellingAddress.name}`,
      extra: (
        <Location
          contactGroupAddress={contact.sellingAddress}
          contactGroupType={contactGroupType}
          size={Size.SM}
        />
      ),
      hide: isAlternateVersionEnabled,
    },
  ];
};
